import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, SlidersHorizontal, Truck } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { LoadingCard, LoadingPage } from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { SoldRibbon } from '../components/SoldRibbon';

const TractorUnitsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Filter for tractor units only
  const apiFilters = useMemo(() => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedMake) filters.make = selectedMake;
    if (selectedYear) filters.year = parseInt(selectedYear);
    return filters;
  }, [searchTerm, selectedMake, selectedYear]);

  const { trucks: allTrucks, loading, error } = useTrucks(apiFilters);

  // Filter for tractor units and apply price range
  const tractorUnits = useMemo(() => {
    let filtered = allTrucks.filter(truck => 
      truck.bodytype && truck.bodytype.toLowerCase().includes('tractor')
    );

    if (priceRange) {
      filtered = filtered.filter(truck => {
        const price = truck.price || 0;
        switch (priceRange) {
          case 'under40k': return price < 40000;
          case '40k-50k': return price >= 40000 && price <= 50000;
          case '50k-60k': return price > 50000 && price <= 60000;
          case 'over60k': return price > 60000;
          default: return true;
        }
      });
    }

    return filtered;
  }, [allTrucks, priceRange]);

  // Get unique values for filters from tractor units
  const makes = [...new Set(tractorUnits.map(truck => truck.make))].sort();
  const years = [...new Set(tractorUnits.map(truck => truck.year))].sort((a, b) => b - a);

  const TruckCard = ({ truck, isListView = false }) => (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group ${
      isListView ? 'flex' : ''
    }`}>
      <div className={`relative ${isListView ? 'w-1/3' : ''}`}>
        <img 
          src={truck.images[0]} 
          alt={truck.title}
          className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
            isListView ? 'w-full h-full' : 'w-full h-48'
          } ${truck.is_sold ? 'grayscale opacity-80' : ''}`}
        />
        {truck.is_sold && <SoldRibbon size="md" />}
        {truck.available_for_hire && (
          <div className="absolute top-4 left-4">
            <div className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold shadow-lg">
              Available for Hire
            </div>
          </div>
        )}
      </div>
      <div className={`p-6 ${isListView ? 'flex-1' : ''}`}>
        <h3 className="text-xl font-bold text-black mb-2 font-inter line-clamp-2">{truck.title}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span>{truck.year}</span>
          <span>•</span>
          <span>{truck.power}</span>
          <span>•</span>
          <span>{truck.axle}</span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2 font-inter">{truck.description}</p>
        
        {isListView && (
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div><strong>Mileage:</strong> {truck.mileage?.toLocaleString()} km</div>
            <div><strong>Emissions:</strong> {truck.emissions}</div>
            <div><strong>Cab Type:</strong> {truck.cabtype}</div>
            <div><strong>Weight:</strong> {truck.weight}t</div>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold text-black">
            {truck.is_sold ? <span className="text-red-600">SOLD</span> : (truck.price ? `£${truck.price.toLocaleString()}` : 'POA')}
          </div>
          <Link 
            to={`/trucks/${truck.id}`}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading && tractorUnits.length === 0) {
    return <LoadingPage message="Loading tractor units..." />;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="MAN Tractor Units for Sale"
        description="Quality MAN tractor units for sale in Leicester. Professional inspection, export worldwide. Perfect for long-haul transport and logistics. Up to 12 months coverage available."
        keywords="MAN tractor units Leicester, tractor units for sale, MAN truck heads, commercial tractor units, long haul trucks"
        canonical="/tractor-units"
      />
      
      {/* Header */}
      <div className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-orange-600 rounded-full p-4">
                <Truck className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black font-inter mb-4">
              Tractor <span className="text-orange-500">Units</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Professional tractor units for long-haul operations. Quality vehicles from trusted manufacturers with full service history.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Search MAN Tractor Units</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by model, year, engine specification, or features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-inter">View:</span>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Make</label>
                <select
                  value={selectedMake}
                  onChange={(e) => setSelectedMake(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Makes</option>
                  {makes.map(make => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Years</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Price Range</label>
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Prices</option>
                  <option value="under40k">Under £40,000</option>
                  <option value="40k-50k">£40,000 - £50,000</option>
                  <option value="50k-60k">£50,000 - £60,000</option>
                  <option value="over60k">Over £60,000</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedMake('');
                    setSelectedYear('');
                    setPriceRange('');
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-bold transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 font-inter">
            Showing {tractorUnits.length} tractor unit{tractorUnits.length !== 1 ? 's' : ''}
            {loading && ' (loading...)'}
          </p>
          {error && (
            <p className="text-red-600 font-inter mt-2">{error}</p>
          )}
        </div>

        {/* Tractor Units Grid/List */}
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
            : 'space-y-6'
        }>
          {loading && tractorUnits.length === 0 ? (
            Array.from({ length: 6 }).map((_, index) => (
              <LoadingCard key={index} />
            ))
          ) : (
            tractorUnits.map((truck) => (
              <TruckCard key={truck.id} truck={truck} isListView={viewMode === 'list'} />
            ))
          )}
        </div>

        {!loading && tractorUnits.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🚛</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2 font-inter">No tractor units found</h3>
            <p className="text-gray-600 font-inter">Try adjusting your search criteria or <Link to="/trucks" className="text-orange-600 hover:underline">browse all trucks</Link>.</p>
          </div>
        )}

        {/* Why Choose Our Tractor Units */}
        {tractorUnits.length > 0 && (
          <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-black text-black mb-6 font-inter text-center">
              Why Choose Our <span className="text-orange-600">Tractor Units</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <div className="text-orange-600 text-2xl font-bold">✓</div>
                </div>
                <h3 className="font-bold text-black mb-2 font-inter">Full Service History</h3>
                <p className="text-gray-600 font-inter">Complete maintenance records and transparent vehicle history.</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <div className="text-orange-600 text-2xl font-bold">🔧</div>
                </div>
                <h3 className="font-bold text-black mb-2 font-inter">Expert Inspection</h3>
                <p className="text-gray-600 font-inter">Thoroughly inspected by our qualified technicians.</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <div className="text-orange-600 text-2xl font-bold">🚚</div>
                </div>
                <h3 className="font-bold text-black mb-2 font-inter">Ready for Work</h3>
                <p className="text-gray-600 font-inter">All vehicles are road-ready and prepared for immediate use.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TractorUnitsPage;